import Container from '@risk-smart/themed-cloudscape-components/container';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useGetThirdPartyById } from 'src/hooks/queries/third-party/useGetThirdPartyById';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useInsertQuestionnaireInvites } from '@/hooks/mutations/questionnaire-invite';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { thirdPartyQuestionnairesUrl } from '@/utils/urls';

import { PageLayout } from '../../../../layouts';
import { InvitationForm } from './forms/InvitationForm';
import type { InvitationFields } from './forms/invitationSchema';
import { defaultValues } from './forms/invitationSchema';

const ThirdPartyInvitePage = () => {
  const thirdPartyId = useGetGuidParam('id');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const navigate = useNavigate();
  const { insertQuestionnaireInvites } = useInsertQuestionnaireInvites();
  const { t } = useTranslation(['common'], { keyPrefix: 'plan_questionnaire' });

  const { data, loading: loadingThirdParty } = useGetThirdPartyById({
    queryArgs: { thirdPartyId },
  });

  const thirdParty = data?.third_party;
  const { hasPermission: canEdit, loading: canEditLoading } =
    useHasPermissionQuery('update:third_party', thirdParty);

  const onDismiss = () => {
    navigate(thirdPartyQuestionnairesUrl(thirdPartyId));
  };

  const onSave = async (data: InvitationFields) => {
    await insertQuestionnaireInvites({
      ThirdPartyId: thirdPartyId,
      UserEmails: data.users.map((u) => u.value),
      QuestionnaireTemplateVersionIds: data.questionnaires,
      Message: data.message,
    });
  };

  return (
    <PageLayout
      title={t('page_title')}
      // Title-only toolbar (CUS-169) — flag-gated. The invitation form keeps its
      // own Submit / Cancel buttons (Cancel returns to the questionnaires tab, so
      // it is not bridged to the toolbar's navigate(-1) Cancel).
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={{ header: { title: t('page_title') } }} />
        ) : undefined
      }
    >
      <Container>
        <InvitationForm
          readOnly={!canEdit || canEditLoading || loadingThirdParty}
          values={{
            ...defaultValues,
          }}
          onSave={onSave}
          onDismiss={onDismiss}
        />
      </Container>
    </PageLayout>
  );
};

export default ThirdPartyInvitePage;
